import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import {
  BEER_INGESTION_RUN_LIST_LIMIT,
  CALLABLE_BEER_GET_BEER_INGESTION_RUN,
  CALLABLE_BEER_LIST_BEER_INGESTION_RUNS,
  CALLABLE_BEER_RETRY_INGESTION_RUN,
  CALLABLE_BEER_LIST_BREWERY_BEER_SOURCES,
  CALLABLE_BEER_RUN_BEER_SOURCE_MONITOR,
  CALLABLE_BEER_RECREATE_BEER_SOURCE_MONITOR,
  CALLABLE_BEER_ENABLE_SRC_MONITORING,
  CALLABLE_BEER_SET_TRUSTED_BREWERY_BEER_SOURCE,
  CALLABLE_BEER_UPDATE_BREWERY_BEER_SOURCE_STATUS,
  CALLABLE_BEER_UPSERT_BREWERY_BEER_SOURCE,
} from '../utils/callableBeerConstants';

function parseIsoDate(value) {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeSourceRecord(rawSource = {}) {
  const sourceType =
    typeof rawSource.sourceType === 'string' && rawSource.sourceType.trim()
      ? rawSource.sourceType.trim()
      : null;
  return {
    ...rawSource,
    url: rawSource.url || rawSource.source || '',
    sourceType,
    createdAt: parseIsoDate(rawSource.createdAt),
    updatedAt: parseIsoDate(rawSource.updatedAt),
    lastCheckedAt: parseIsoDate(rawSource.lastCheckedAt),
    lastChangedAt: parseIsoDate(rawSource.lastChangedAt),
    nextCheckAt: parseIsoDate(rawSource.nextCheckAt),
    pendingManualCheckRequestedAt: parseIsoDate(rawSource.pendingManualCheckRequestedAt),
    lastExtractBeerCount: normalizeExtractBeerCount(rawSource.lastExtractBeerCount),
  };
}

function normalizeExtractBeerCount(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return Math.floor(value);
}

function normalizeRunRecord(rawRun = {}) {
  const runKind =
    typeof rawRun.runKind === 'string' && rawRun.runKind.trim()
      ? rawRun.runKind.trim()
      : 'import';
  return {
    ...rawRun,
    runKind,
    startedAt: parseIsoDate(rawRun.startedAt),
    completedAt: parseIsoDate(rawRun.completedAt),
    beersExtractedCount: Number(rawRun.beersExtractedCount) || 0,
    beersValidCount: Number(rawRun.beersValidCount) || 0,
    beersFlaggedCount: Number(rawRun.beersFlaggedCount) || 0,
    beersInvalidCount: Number(rawRun.beersInvalidCount) || 0,
    beersMissingAbvCount: Number(rawRun.beersMissingAbvCount) || 0,
    beersMissingStyleCount: Number(rawRun.beersMissingStyleCount) || 0,
    hasSnapshot: rawRun.hasSnapshot === true,
    hasValidatedSnapshot: rawRun.hasValidatedSnapshot === true,
    hasComparison: rawRun.hasComparison === true,
    comparisonVersion: rawRun.comparisonVersion ?? null,
    baselineRunId: rawRun.baselineRunId ?? null,
    comparisonErrorMessage: rawRun.comparisonErrorMessage ?? null,
    candidatesNewCount: Number(rawRun.candidatesNewCount) || 0,
    candidatesChangedCount: Number(rawRun.candidatesChangedCount) || 0,
    candidatesMissingCount: Number(rawRun.candidatesMissingCount) || 0,
    candidatesRenameCount: Number(rawRun.candidatesRenameCount) || 0,
    candidatesInvalidCount: Number(rawRun.candidatesInvalidCount) || 0,
    candidatesUnchangedCount: Number(rawRun.candidatesUnchangedCount) || 0,
    reviewStatus: rawRun.reviewStatus ?? null,
    reviewPendingCount: Number(rawRun.reviewPendingCount) || 0,
    reviewDecidedCount: Number(rawRun.reviewDecidedCount) || 0,
    reviewedAt: parseIsoDate(rawRun.reviewedAt),
    reviewedBy: rawRun.reviewedBy ?? null,
    costCredits:
      typeof rawRun.costCredits === 'number' ? rawRun.costCredits : null,
  };
}

export async function listBreweryBeerSources(breweryId) {
  const listFn = httpsCallable(functions, CALLABLE_BEER_LIST_BREWERY_BEER_SOURCES);
  const result = await listFn({ breweryId });
  const data = result.data ?? {};
  const rows = Array.isArray(data.sources) ? data.sources : [];
  return rows.map(normalizeSourceRecord);
}

export async function upsertBreweryBeerSource(payload) {
  const upsertFn = httpsCallable(functions, CALLABLE_BEER_UPSERT_BREWERY_BEER_SOURCE);
  const result = await upsertFn(payload);
  const data = result.data ?? {};
  return normalizeSourceRecord(data.source ?? {});
}

export async function setTrustedBreweryBeerSource(breweryId, sourceId) {
  const setTrustedFn = httpsCallable(
    functions,
    CALLABLE_BEER_SET_TRUSTED_BREWERY_BEER_SOURCE,
  );
  const result = await setTrustedFn({ breweryId, sourceId });
  const data = result.data ?? {};
  return normalizeSourceRecord(data.source ?? {});
}

export async function updateBreweryBeerSourceStatus(sourceId, monitoringStatus) {
  const updateStatusFn = httpsCallable(
    functions,
    CALLABLE_BEER_UPDATE_BREWERY_BEER_SOURCE_STATUS,
  );
  const result = await updateStatusFn({ sourceId, monitoringStatus });
  const data = result.data ?? {};
  return normalizeSourceRecord(data.source ?? {});
}

export async function listBeerIngestionRunsForSource(sourceId, limit = 1) {
  const listRunsFn = httpsCallable(functions, CALLABLE_BEER_LIST_BEER_INGESTION_RUNS);
  const result = await listRunsFn({ sourceId, limit });
  const data = result.data ?? {};
  const rows = Array.isArray(data.runs) ? data.runs : [];
  return rows.map(normalizeRunRecord);
}

export async function listBeerIngestionRunsForBrewery(
  breweryId,
  runKind,
  limit = BEER_INGESTION_RUN_LIST_LIMIT,
) {
  const listRunsFn = httpsCallable(functions, CALLABLE_BEER_LIST_BEER_INGESTION_RUNS);
  const payload = { breweryId, limit };
  if (runKind) {
    payload.runKind = runKind;
  }
  const result = await listRunsFn(payload);
  const data = result.data ?? {};
  const rows = Array.isArray(data.runs) ? data.runs : [];
  return rows.map(normalizeRunRecord);
}

export async function getBeerIngestionRun(runId) {
  const getRunFn = httpsCallable(functions, CALLABLE_BEER_GET_BEER_INGESTION_RUN);
  const result = await getRunFn({ runId });
  const data = result.data ?? {};
  const rawRun = data.run && typeof data.run === 'object' ? data.run : data;
  const topLevelName = typeof data.breweryName === 'string' ? data.breweryName.trim() : '';
  const runName = typeof rawRun.breweryName === 'string' ? rawRun.breweryName.trim() : '';
  const breweryName = runName || topLevelName || null;
  const normalizedRun = normalizeRunRecord(rawRun);
  if (breweryName) {
    normalizedRun.breweryName = breweryName;
  } else if (typeof normalizedRun.breweryName === 'string') {
    const trimmedRunName = normalizedRun.breweryName.trim();
    normalizedRun.breweryName = trimmedRunName || null;
  }
  return {
    run: normalizedRun,
    snapshot: data.snapshot ?? null,
    comparison: data.comparison ?? null,
    review: data.review ?? null,
    breweryName,
  };
}

export async function retryBeerIngestionProcessing(runId) {
  const retryFn = httpsCallable(functions, CALLABLE_BEER_RETRY_INGESTION_RUN);
  const result = await retryFn({ runId });
  const data = result.data ?? {};
  return normalizeRunRecord(data.run ?? {});
}

export async function runBeerSourceMonitor(sourceId) {
  const runFn = httpsCallable(functions, CALLABLE_BEER_RUN_BEER_SOURCE_MONITOR);
  const result = await runFn({ sourceId });
  const data = result.data ?? {};
  return {
    source: normalizeSourceRecord(data.source ?? {}),
    checkId: data.checkId ?? null,
  };
}

export async function recreateBeerSourceMonitor(sourceId) {
  const recreateFn = httpsCallable(functions, CALLABLE_BEER_RECREATE_BEER_SOURCE_MONITOR);
  const result = await recreateFn({ sourceId });
  const data = result.data ?? {};
  return {
    source: normalizeSourceRecord(data.source ?? {}),
    checkId: data.checkId ?? null,
  };
}

export async function enableBeerSourceMonitoring(payload) {
  const enableFn = httpsCallable(functions, CALLABLE_BEER_ENABLE_SRC_MONITORING);
  const result = await enableFn(payload);
  const data = result.data ?? {};
  const sources = Array.isArray(data.sources) ? data.sources : [];
  return {
    attempted: Number(data.attempted) || 0,
    succeeded: Number(data.succeeded) || 0,
    failed: Array.isArray(data.failed) ? data.failed : [],
    sources: sources.map(normalizeSourceRecord),
  };
}
